<script>
import { mapGetters, mapActions } from 'vuex';
import { useAlert } from 'dashboard/composables'; 
import AssignStaffModal from './assignStaffModal.vue';
import DeleteConfirmationModal from './deleteConfirmationModal.vue';

export default {
  props: {
    scheduleId: {
      type: String,
      default: null,
    },
    branchId: {
      type: String,
      default: null,
    }
  },
  components: {
    AssignStaffModal,
    DeleteConfirmationModal,
  },
  data() {
    return {
      isAssigningStaff: false,
      deleteStaffObject: null,
    };
  },
  computed: {
    ...mapGetters('appointments', ['staffList']),
    assignedStaffList() {
      return this.staffList.results.filter(staff =>
        staff.schedules.some(item => item.schedule_id === this.scheduleId)
      );
    },
  },
  methods: {
    ...mapActions('appointments', ['removeStaffSchedule', 'fetchStaffList']),
    resetAssigningStaff() {
      this.isAssigningStaff = false;
    },
    async confirmDelete({ staffId, scheduleId }) {
      try {
        await this.removeStaffSchedule({ staffId, scheduleId });
        this.deleteStaffObject = null;
        useAlert(this.$t('BRANCHES.VIEW_ASSIGNED_STAFF.STAFF_REMOVED'));
      } catch (err) {
        console.error(err);
        useAlert(this.$t('BRANCHES.VIEW_ASSIGNED_STAFF.ERROR_REMOVING_STAFF'));
      } finally {
        await this.fetchStaffList(this.branchId);
      }
    }
  },
  async mounted() {
    try {
      await this.fetchStaffList(this.branchId);
    } catch (err) {
      console.error(err);
      useAlert(err); // TODO: optionally i18n this
    }
  }
};
</script>

<template>
  <div class="p-4 bg-white dark:bg-slate-700 rounded-md border-2 border-slate-100 dark:border-slate-50">
    <!-- Delete Staff Modal -->
    <div 
      v-if="deleteStaffObject" 
      class="fixed inset-0 flex items-center justify-center h-[100vh] w-[100vw] bg-slate-900/50 dark:bg-slate-800/50 z-50"
    >
      <DeleteConfirmationModal
        :deleteName="deleteStaffObject.name"
        @confirm-delete="confirmDelete({
          staffId: deleteStaffObject.staffId,
          scheduleId: deleteStaffObject.scheduleId
        })"
        @close="deleteStaffObject = null"
      />
    </div>

    <!-- Assign Staff Modal -->
    <div 
      v-if="isAssigningStaff" 
      class="fixed inset-0 flex items-center justify-center h-[100vh] w-[100vw] bg-slate-900/50 dark:bg-slate-800/50 z-50"
    >
      <AssignStaffModal
        :scheduleId="scheduleId"
        :resetAssigningStaff="resetAssigningStaff" 
      />
    </div>

    <div class="flex justify-between items-center mb-4">
      <h1 class="text-xl font-semibold text-gray-900">
        {{ $t('BRANCHES.VIEW_ASSIGNED_STAFF.TITLE') }}
      </h1>
      <woot-button
        variant="smooth"
        color-scheme="primary"
        icon="plus-sign"
        @click="isAssigningStaff = true"
      >
        {{ $t('BRANCHES.VIEW_ASSIGNED_STAFF.ADD_STAFF') }}
      </woot-button>
    </div> 

    <div 
      v-if="assignedStaffList.length === 0"
      class="text-center bg-gray-50 dark:bg-slate-800 p-8 rounded-lg border border-gray-200 dark:border-gray-700"
    >
      <fluent-icon icon="people" class="h-12 w-12 mx-auto text-gray-400 dark:text-gray-500 mb-4" />
      <p class="text-gray-600 dark:text-gray-300 mb-2">
        {{ $t('BRANCHES.VIEW_ASSIGNED_STAFF.NO_STAFF') }}
      </p>
      <p class="text-sm text-gray-500 dark:text-gray-400">
        {{ $t('BRANCHES.VIEW_ASSIGNED_STAFF.ADD_STAFF_PROMPT') }}
      </p>
    </div>

    <div v-if="assignedStaffList.length > 0" class="flex gap-4 w-full flex-wrap mt-4">
      <div
        class="flex items-center justify-between gap-3 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 px-4 py-2"
        v-for="assignedStaff in assignedStaffList"
        :key="assignedStaff.id"
      >
        <span class="text-sm font-medium text-gray-800 dark:text-gray-100">
          {{ assignedStaff.staff_name }}
        </span>

        <woot-button
          variant="clear"
          size="tiny"
          color-scheme="alert"
          icon="delete"
          @click="deleteStaffObject = {
            name: assignedStaff.staff_name,
            staffId: assignedStaff.staff_id,
            scheduleId: assignedStaff.schedules[0].schedule_id
          }"
        />
      </div>
    </div>
  </div>
</template>
